import { useCallback } from 'react'
import { useReactiveVar } from '@apollo/client'
import { Button, Flex, Heading, Text } from '@radix-ui/themes'
import { WizardSubmitButton } from 'features/auth/components/wizard-submit-button'
import { DataTestIDs } from 'shared/constants'
import { XyroDialog } from 'shared/ui'
import { formatLongDate } from 'shared/utils/format-date'
import { getPrivySessionBridge } from '../session-bridge'
import { isPrivyExchangingVar, privyRestoreAccountVar } from '../store'

/**
 * Offers to restore an account that waits for its deletion (FE-331):
 * `signInPrivy` refused it, Restore signs in again with `restoreAccount`.
 * Closing the dialog keeps the deletion and drops the Privy session, so the
 * visitor stays a guest.
 */
export const PrivyRestoreAccountDialog = () => {
  const pending = useReactiveVar(privyRestoreAccountVar)
  const isExchanging = useReactiveVar(isPrivyExchangingVar)

  const handleRestore = useCallback(() => {
    void getPrivySessionBridge()?.restoreAccount()
  }, [])

  // NOTE: not while the restore is on its way: it may already have cancelled
  // the deletion, and the Privy session must outlive it
  const handleOpenChange = useCallback((nextIsOpen: boolean) => {
    if (nextIsOpen || isPrivyExchangingVar()) return

    privyRestoreAccountVar(null)
    void getPrivySessionBridge()?.logout()
  }, [])

  const scheduledFor = pending?.scheduledFor ?? null
  const deletionText =
    scheduledFor !== null ?
      `Your account will be deleted on ${formatLongDate(scheduledFor)}.`
    : 'Your account is scheduled for deletion.'

  return (
    <XyroDialog
      open={pending !== null}
      onOpenChange={handleOpenChange}
    >
      <Flex
        direction='column'
        align='center'
        gap='3'
        p='6'
        data-testid={DataTestIDs.privyRestoreAccountDialog}
      >
        <Heading
          size='6'
          className='color-white'
        >
          Restore your account?
        </Heading>

        <Text
          size='2'
          align='center'
          className='color-white'
        >
          {deletionText} Restore it to keep your profile, history and balances.
          Until then you stay signed out.
        </Text>

        <WizardSubmitButton
          type='button'
          onClick={handleRestore}
          isLoading={isExchanging}
          data-testid={DataTestIDs.buttonPrivyRestoreAccount}
        >
          Restore account
        </WizardSubmitButton>

        <Button
          variant='ghost'
          color='gray'
          size='3'
          type='button'
          disabled={isExchanging}
          onClick={() => handleOpenChange(false)}
          data-testid={DataTestIDs.buttonPrivyRestoreDecline}
        >
          Not now
        </Button>
      </Flex>
    </XyroDialog>
  )
}
